import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { logOut } from '~/actions';
import Avatar from '~/components/Avatar';
import Background from '~/components/Background';
import Button from '~/components/Button';
import Cardbox from '~/components/Cardbox';
import CardboxWrapper from '~/components/CardboxWrapper';
import DropdownInput from '~/components/DropdownInput';
import Gap from '~/components/Gap';
import StyledInput from '~/components/StyledInput';
import Text from '~/components/Text';
import {
  COUNTRIES,
  COUNTRIES_CODES,
  getCountryCodeByCountryName,
  getCurrencyByCountry,
} from '~/constants/address';
import * as Styles from './styles';
import { useAddBusiness } from './useAddBusiness';
import { useIsDesktop } from '~/hooks/useIsDesktop';

const AccountSetup = () => {
  const { business, isSaving, onChange, saveBusiness } = useAddBusiness({ isAccountSetup: true });
  const dispatch = useDispatch();
  const isDesktop = useIsDesktop();

  const handleClickLogout = () => {
    dispatch(logOut());
  };

  return (
    <Background key="AccountSetup" data-component-name="AccountSetup">
      <CardboxWrapper isPadded={isDesktop ? true : false}>
        <Styles.HalfContainer className="full-dimensions full-flex center">
          <Cardbox
            width={isDesktop ? '532px' : '100%'}
            className="v-d-flex"
            isDesktopStyle={isDesktop ? true : false}
          >
            <div>
              <Text align="center" size={isDesktop ? 'lg' : 'md'} className="bold">
                Set up your account
              </Text>
              <Gap t={16} />
              <Text align="center">
                Please enter some information about yourself and your company.
              </Text>
              <Gap t={24} />
            </div>

            <div className="full-flex has-y-scroll">
              <div className="full-width align-center">
                <div className="full-flex">
                  <Text size="xs">
                    First Name <span className="danger">*</span>
                  </Text>
                  <Gap t={6} />
                  <StyledInput
                    value={business.first_name}
                    onChange={e => onChange({ first_name: e.target.value })}
                    className={`${!business.first_name && 'error-field'}`}
                  />
                </div>

                <Gap r={16} />

                <div className="full-flex">
                  <Text size="xs">
                    Last Name <span className="danger">*</span>
                  </Text>
                  <Gap t={6} />
                  <StyledInput
                    value={business.last_name}
                    onChange={e => onChange({ last_name: e.target.value })}
                    className={`${!business.last_name && 'error-field'}`}
                  />
                </div>
              </div>

              <Gap t={16} />

              <Text size="xs">
                Company Name <span className="danger">*</span>
              </Text>
              <Gap t={6} />
              <StyledInput
                value={business.company_name}
                onChange={e => onChange({ company_name: e.target.value })}
                className={`${!business.company_name && 'error-field'}`}
              />

              <Gap t={28} />

              <Text size={isDesktop ? 'md' : 'ssmd'} className="semi-bold">
                Company Address Information
              </Text>

              <Gap t={28} />

              <Text size="xs">
                Country <span className="danger">*</span>
              </Text>
              <Gap t={4} />
              <DropdownInput
                search
                value={business.country}
                className={`${!business.country && 'error-field'}`}
                onChange={value => {
                  onChange({ country: value });
                  if (!business.currency) {
                    const countryCurrency = getCurrencyByCountry(value);
                    onChange({ currency: countryCurrency });
                  }
                  const countryCode = getCountryCodeByCountryName(value);
                  if (countryCode) {
                    onChange({ country_code: countryCode });
                  }
                }}
                options={COUNTRIES}
              />

              <Gap t={16} />
              <div className="center">
                <div className="full-flex" style={{ width: '40%' }}>
                  <Text size="xs">Country Code</Text>
                  <Gap t={4} />
                  <DropdownInput
                    search
                    value={business.country_code || ''}
                    onChange={value => {
                      onChange({ country_code: value });
                    }}
                    options={COUNTRIES_CODES}
                    showSelection={false}
                  />
                </div>
                <Gap r={16} />
                <div className="full-flex" style={{ width: '60%' }}>
                  <Text size="xs">Phone</Text>
                  <Gap t={4} />
                  <StyledInput
                    value={business.phone}
                    onChange={e => onChange({ phone: e.target.value })}
                  />
                </div>
              </div>

              <Gap t={16} />
              <Text size="xs">Address Line 1</Text>
              <Gap t={6} />
              <StyledInput
                value={business.address_line_1}
                onChange={e => onChange({ address_line_1: e.target.value })}
              />

              <Gap t={16} />

              <div className="center">
                <div className="full-flex">
                  <Text size="xs">Address Line 2</Text>
                  <Gap t={4} />
                  <StyledInput
                    value={business.address_line_2}
                    onChange={e => onChange({ address_line_2: e.target.value })}
                  />
                </div>
                <Gap r={16} />
                <div className="full-flex">
                  <Text size="xs">City</Text>
                  <Gap t={4} />
                  <StyledInput
                    value={business.city}
                    onChange={e => onChange({ city: e.target.value })}
                  />
                </div>
              </div>

              <Gap t={16} />
              <div className="center">
                <div className="full-flex">
                  <Text size="xs">State / Province</Text>
                  <Gap t={4} />
                  <StyledInput
                    value={business.state}
                    onChange={e => onChange({ state: e.target.value })}
                  />
                </div>
                <Gap r={16} />
                <div className="full-flex">
                  <Text size="xs">Postal code</Text>
                  <Gap t={4} />
                  <StyledInput
                    value={business.postal_code}
                    onChange={e => onChange({ postal_code: e.target.value })}
                  />
                </div>
              </div>

              <Gap t={50} />
            </div>

            <Button size="full" disabled={isSaving} onClick={saveBusiness}>
              {isSaving ? <LoadingOutlined /> : 'Continue'}
            </Button>
          </Cardbox>
        </Styles.HalfContainer>
        {isDesktop && (
          <Styles.HalfContainer className="full-dimensions full-flex center no-padding">
            <Styles.ContentContainer className="v-center">
              <Gap t={180} />
              <Avatar imageUrl="/media/images/avatar.png" fullName="John Doe" />
              <Gap t={24} />
              <Text align="center" className="semi-bold">
                Nothing short of groundbreaking for business owners. It immediately changes how i
                work and make me 10x more productive as a business owner
              </Text>
              <Gap t={32} />
              <Text className="semi-bold">@ransegall</Text>
              <Gap t={16} />
              <Text className="semi-bold" variant="secondary">
                Founder, Flux Academy
              </Text>

              <Gap t={40} />

              <Button variant="secondary" onClick={handleClickLogout}>
                Log out
              </Button>
            </Styles.ContentContainer>
          </Styles.HalfContainer>
        )}
      </CardboxWrapper>
    </Background>
  );
};

export default AccountSetup;
